// React
import React, { Fragment, useEffect, useState } from "react";

// Material-UI
import Card from "@material-ui/core/Card";
import CardActions from "@material-ui/core/CardActions";
import CardMedia from "@material-ui/core/CardMedia";
import IconButton from "@material-ui/core/IconButton";
import { makeStyles } from "@material-ui/core/styles";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";

// Apollo Client
import { useMutation, gql, useQuery } from "@apollo/client";

// Custom
import { notify } from "../Notify/Notify";
import { uploadClient } from "../..";

// External Components
import { Spinner } from "react-bootstrap";

// Define the mutation
const SAVE_ORGANIZATION = gql`
  mutation SaveOrganization($name: String!, $logo: Upload!) {
    saveOrganization(name: $name, logo: $logo) {
      success
      organization {
        id
        name
        logo
        preSignedUrl
      }
    }
  }
`;

// Define the GraphQL query
const GET_ORGANIZATION = gql`
  query {
    data: getOrganization {
      initialOrganizationName: name
      logo
      imageUrl: preSignedUrl
    }
  }
`;

const useStyles = makeStyles((theme) => ({
  root: {
    maxWidth: 345,
    marginTop: "24px",
    marginBottom: "24px",
  },
  media: {
    height: 0,
    paddingTop: "56.25%", // 16:9
  },
  textInput: {
    maxWidth: 345,
  },
  input: {
    display: "none", // Hide the input element
  },
  footer: {
    display: "flex",
    flexDirection: "row-reverse",
    alignItems: "center",
    borderTop: "1px solid #ccc",
    padding: "24px",
    backgroundColor: "#fff",
    width: "100%",
    position: "fixed",
    bottom: 0,
    left: 0,
  },
  container: {
    maxHeight: "calc(100vh - 260px)",
    overflowY: "auto", // Add scroll when content overflows
    paddingBottom: "60px",
  },
}));

function OrganizationSettings() {
  const classes = useStyles();

  const { data: organizationData, loading: isLoadingOrganization } = useQuery(
    GET_ORGANIZATION,
    {
      client: uploadClient,
      fetchPolicy: "network-only",
    }
  );

  const { initialOrganizationName, imageUrl } = organizationData?.data ?? {};

  const [organizationName, setOrganizationName] = useState("");
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaOriginalFile, setMediaOriginalFile] = useState(null);

  // Function to handle file selection
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setMediaFile(URL.createObjectURL(file));
      setMediaOriginalFile(file);
    } else {
      notify("Error", "Something went wrong.", "error");
    }
  };

  // Mutations
  const [saveOrganization, { loading: isSaveOrganizationLoading }] =
    useMutation(SAVE_ORGANIZATION, {
      client: uploadClient,
      refetchQueries: [{ query: GET_ORGANIZATION }],
      onError: (error) => {
        notify("Error", "Something went wrong.", "error");
        console.error("Save Organization Mutation Error:", error);
      },
      onCompleted: () => {
        notify("Success", "Organization data is successfully saved", "success");
        setMediaFile(null);
        setMediaOriginalFile(null);
      },
      awaitRefetchQueries: true,
    });

  const isSaveBtnDisabled =
    (organizationName === "" || initialOrganizationName === organizationName) &&
    mediaFile === null;

  // Effects
  useEffect(() => {
    if (initialOrganizationName !== organizationName) {
      setOrganizationName(initialOrganizationName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialOrganizationName, setOrganizationName]);

  return (
    <div>
      <div className={classes.container}>
        <Card className={classes.root}>
          <CardMedia
            className={classes.media}
            image={mediaFile || imageUrl}
            title="Paella dish"
          />
          <CardActions
            disableSpacing
            style={{
              justifyContent: isLoadingOrganization ? "center" : "flex-end",
            }}
          >
            {isLoadingOrganization && (
              <Spinner animation="border" role="status" />
            )}
            {!isLoadingOrganization && (
              <Fragment>
                <label htmlFor="fileInput">
                  <IconButton aria-label="edit media" component="span">
                    <EditIcon />
                  </IconButton>
                </label>
                <input
                  accept="image/*"
                  id="fileInput"
                  type="file"
                  className={classes.input}
                  onChange={handleFileChange}
                />
                <label>
                  <IconButton
                    aria-label="delete media"
                    component="span"
                    disabled={mediaFile === null}
                    onClick={() => setMediaFile(null)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </label>
              </Fragment>
            )}
          </CardActions>
        </Card>
        <div style={{ marginTop: "24px" }}>
          <label>Organization Name *</label>
          <input
            type="text"
            placeholder="Enter organization name"
            value={organizationName}
            maxLength="50"
            className={classes.textInput}
            disabled={isLoadingOrganization}
            onChange={(e) => setOrganizationName(e.target.value)}
          />
        </div>
      </div>
      <div className={classes.footer}>
        <button
          type="submit"
          className="btn-primary"
          disabled={isSaveBtnDisabled || isSaveOrganizationLoading}
          onClick={() => {
            saveOrganization({
              variables: {
                name: organizationName,
                logo: mediaOriginalFile,
              },
            });
          }}
        >
          Save
        </button>
      </div>
    </div>
  );
}

export default OrganizationSettings;
